import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, GitMerge } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { type MergeField, type MergeFieldRow, type MergePreview } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Radio, RadioGroup } from '../../../components/Checkbox';
import { SaveBar } from '../../../components/SaveBar';
import { SkeletonLines } from '../../../components/Skeleton';
import { ForbiddenState, NotFoundState, ServerErrorState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { formatDate } from '../../../lib/format';
import { useToasts } from '../../../stores/toasts';
import { useMerge, useMergePreview } from '../api/candidates-api';
import profile from './CandidateProfile.module.css';
import { StageChip } from '../components/CandidateBits';
import styles from './DuplicatesPage.module.css';

export interface MergeSearch {
  readonly a?: string;
  readonly b?: string;
  readonly flag?: string;
}

type Side = 'survivor' | 'merged';

/**
 * F7.07 — merging two records into one (§L2). The older record always survives; the
 * recruiter only decides what §L2 says a person must decide: a field both records
 * hold differently, and — when both applied to the same job — which application
 * stays (DECISIONS-LOG S14 Q3). The other closes as a duplicate, never a rejection
 * anyone reports on.
 */
export function MergePage({ search }: { readonly search: MergeSearch }): ReactNode {
  if (!search.a || !search.b) return <NotFoundState />;
  return <MergeReview first={search.a} second={search.b} flagId={search.flag ?? null} />;
}

function MergeReview({ first, second, flagId }: { readonly first: string; readonly second: string; readonly flagId: string | null }): ReactNode {
  const preview = useMergePreview(first, second);

  if (preview.isPending) return <div className={styles.page}><SkeletonLines count={10} /></div>;
  if (preview.error) {
    if (preview.error.status === 403) return <ForbiddenState resource="merging candidates" contact="Your admin" />;
    if (preview.error.status === 404) return <NotFoundState />;
    return <ServerErrorState onRetry={() => void preview.refetch()} />;
  }
  return <MergeForm preview={preview.data} flagId={flagId} />;
}

function MergeForm({ preview, flagId }: { readonly preview: MergePreview; readonly flagId: string | null }): ReactNode {
  const [survivor, merged] = preview.candidates;
  const [picks, setPicks] = useState<Partial<Record<MergeField, Side>>>({});
  const [keep, setKeep] = useState<Readonly<Record<string, string>>>({});
  const merge = useMerge();
  const show = useToasts((state) => state.show);
  const navigate = useNavigate();

  const needsPick = preview.fields.filter((row) => row.choice === 'pick');
  const isComplete =
    preview.blockers.length === 0 &&
    needsPick.every((row) => picks[row.field] !== undefined) &&
    preview.clashes.every((clash) => keep[clash.jobId] !== undefined);

  async function submit(): Promise<void> {
    if (!isComplete) return;
    try {
      const result = await merge.mutateAsync({
        candidateIds: [survivor.id, merged.id],
        versions: [preview.survivorVersion, preview.mergedVersion],
        flagId,
        picks,
        keep,
      });
      show({ tone: 'success', message: 'Records merged', detail: 'HR can undo this for 30 days from the profile.' });
      void navigate({ to: '/candidates/$candidateId', params: { candidateId: result.survivorId } });
    } catch (cause) {
      show({ tone: 'danger', message: "The records weren't merged", detail: errorMessage(cause) });
    }
  }

  return (
    <form
      className={styles.page}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          void submit();
        }
      }}
    >
      <header className={styles.header}>
        <Link to={flagId ? '/candidates/duplicates' : '/candidates/$candidateId'} params={{ candidateId: survivor.id }} className={styles.back}>
          <ArrowLeft size={14} aria-hidden />
          {flagId ? 'Possible duplicates' : survivor.fullName}
        </Link>
        <h1 className={styles.title}>Merge two records</h1>
        <p className={styles.meta}>
          <strong>{survivor.fullName}</strong> (added {formatDate(survivor.createdAt)}) is the older record, so it stays. Everything on{' '}
          <strong>{merged.fullName}</strong> (added {formatDate(merged.createdAt)}) moves onto it: applications, CVs, skills, referrals and stars.
        </p>
      </header>

      {preview.blockers.map((blocker) => (
        <p key={blocker} className={styles.blocker}>
          {blocker}
        </p>
      ))}

      <Card title="Details" meta={needsPick.length > 0 ? `Choose which value to keep for ${needsPick.length} ${needsPick.length === 1 ? 'field' : 'fields'}` : 'Nothing to choose — the details agree or fill each other in'}>
        <div className={styles.tableWrap}>
          <table className={styles.mergeTable}>
            <thead>
              <tr>
                <th scope="col">Field</th>
                <th scope="col">Stays: {survivor.fullName}</th>
                <th scope="col">Merges in: {merged.fullName}</th>
              </tr>
            </thead>
            <tbody>
              {preview.fields
                .filter((row) => row.survivorValue !== null || row.mergedValue !== null || row.isRestricted)
                .map((row) => (
                  <FieldRow key={row.field} row={row} pick={picks[row.field]} onPick={(side) => setPicks((current) => ({ ...current, [row.field]: side }))} />
                ))}
            </tbody>
          </table>
        </div>
      </Card>

      {preview.clashes.length > 0 && (
        <Card title="Both applied to the same job" meta="Keep one application. The other closes as a duplicate, which reports ignore">
          <div className={styles.clashes}>
            {preview.clashes.map((clash) => (
              <fieldset key={clash.jobId} className={styles.clash}>
                <legend>
                  {clash.jobTitle} · <span className={styles.reference}>{clash.jobReference}</span>
                </legend>
                <RadioGroup>
                  {clash.applications.map((application) => (
                    <Radio
                      key={application.id}
                      name={`keep-${clash.jobId}`}
                      value={application.id}
                      checked={keep[clash.jobId] === application.id}
                      onChange={(value) => setKeep((current) => ({ ...current, [clash.jobId]: value }))}
                      label={
                        <span className={styles.clashOption}>
                          <span className={styles.reference}>{application.reference}</span>
                          <StageChip stage={application.stage} />
                        </span>
                      }
                      description={`Applied ${formatDate(application.appliedAt)} · on ${application.side === 'survivor' ? survivor.fullName : merged.fullName}`}
                    />
                  ))}
                </RadioGroup>
              </fieldset>
            ))}
          </div>
        </Card>
      )}

      <SaveBar status="HR can undo a merge for 30 days.">
        <Button variant="solid" type="submit" icon={GitMerge} disabled={!isComplete} isPending={merge.isPending}>
          Merge records
        </Button>
      </SaveBar>
      {!isComplete && preview.blockers.length === 0 && <p className={profile.quiet}>Make every choice above to merge.</p>}
    </form>
  );
}

function FieldRow({ row, pick, onPick }: { readonly row: MergeFieldRow; readonly pick: Side | undefined; readonly onPick: (side: Side) => void }): ReactNode {
  if (row.isRestricted) {
    return (
      <tr>
        <td className={styles.fieldLabel}>{row.label}</td>
        <td colSpan={2}>— Not visible to your role; the record that stays keeps its own</td>
      </tr>
    );
  }
  if (row.choice === 'pick') {
    return (
      <tr className={styles.needsPick}>
        <td className={styles.fieldLabel}>{row.label}</td>
        {(['survivor', 'merged'] as const).map((side) => (
          <td key={side}>
            <Radio
              name={`pick-${row.field}`}
              value={side}
              checked={pick === side}
              onChange={() => onPick(side)}
              label={(side === 'survivor' ? row.survivorValue : row.mergedValue) ?? '—'}
            />
          </td>
        ))}
      </tr>
    );
  }
  // "same": both hold it. Otherwise §L2 keeps the side that has a value.
  const isSurvivorKept = row.choice !== 'merged';
  const isMergedKept = row.choice !== 'survivor';
  return (
    <tr>
      <td className={styles.fieldLabel}>{row.label}</td>
      <td className={isSurvivorKept ? styles.kept : styles.dropped}>{row.survivorValue ?? '—'}</td>
      <td className={isMergedKept ? styles.kept : styles.dropped}>{row.mergedValue ?? '—'}</td>
    </tr>
  );
}
